import React, { useState } from 'react';
import { Button, List, Typography, Switch, Modal, Form, Select, Alert, Checkbox, Space } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import ParameterEditor from './ParameterEditor';
import { useScenarioItemValidator } from './hooks/useScenarioItemValidator';
import { Trans, useTranslation } from 'react-i18next';
import { SCENARIO_THEME } from './scenarioTheme';
const { Title } = Typography;
const Step_Actions = ({ isOrdered, onOrderChange, list, onListChange, availableActions, availableProviders }) => {
    const { t } = useTranslation();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingActionInfo, setEditingActionInfo] = useState(null);
    const [modalActionData, setModalActionData] = useState({});
    const [form] = Form.useForm();
    const theme = SCENARIO_THEME.actions;
    const IconComponent = theme.icon;
    const handleAddAction = () => {
        setEditingActionInfo(null);
        setModalActionData({ params: {}, confidentialData: false });
        form.resetFields();
        setIsModalVisible(true);
    };
    const handleEditAction = (action, index) => {
        setEditingActionInfo({ index, data: action });
        setModalActionData(action);
        form.setFieldsValue({ name: action.name, confidentialData: action.confidentialData || false });
        setIsModalVisible(true);
    };
    const handleCancel = () => {
        setIsModalVisible(false);
    };
    const handleOk = () => {
        form.validateFields().then(values => {
            const finalAction = {
                name: values.name,
                params: modalActionData.params || {},
                confidentialData: values.confidentialData || false,
            };
            const newList = [...list];
            if (editingActionInfo) {
                newList[editingActionInfo.index] = finalAction;
            }
            else {
                newList.push(finalAction);
            }
            onListChange(newList);
            handleCancel();
        });
    };
    const handleRemoveAction = (index) => {
        const newList = [...list];
        newList.splice(index, 1);
        onListChange(newList);
    };
    const handleActionTypeChange = (name) => {
        setModalActionData({ name, params: {} });
    };
    const selectedActionDef = availableActions.find(action => action.name === modalActionData.name);
    const isEditing = editingActionInfo !== null;
    const isOkDisabled = useScenarioItemValidator({
        isModalVisible,
        isMainFormValid: true,
        modalData: modalActionData,
        selectedDef: selectedActionDef,
    });
    return (React.createElement("div", null,
        React.createElement(Space, { style: { marginBottom: 24 } },
            React.createElement(IconComponent, { style: { fontSize: '20px', color: theme.color } }),
            React.createElement(Title, { level: 4, style: { margin: 0, color: theme.color } }, theme.name)),
        React.createElement("div", { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 } },
            React.createElement(Typography.Paragraph, { style: { margin: 0, color: '#666' } },
                React.createElement(Trans, { i18nKey: "actionsStepDescription", defaults: 'Define the actions the scenario will perform. They will run sequentially if "Run in order" is enabled.' })),
            React.createElement(Form.Item, { label: React.createElement(Trans, { i18nKey: "runInOrder", defaults: "Run in order" }), style: { margin: 0 } },
                React.createElement(Switch, { checked: isOrdered, onChange: onOrderChange }))),
        React.createElement(List, { bordered: true, dataSource: list, renderItem: (action, index) => {
                const actionDef = availableActions.find(a => a.name === action.name);
                const title = actionDef ? actionDef.title : action.name;
                return (React.createElement(List.Item, { actions: [
                        React.createElement(Button, { type: "link", onClick: () => handleEditAction(action, index) },
                            React.createElement(Trans, { i18nKey: "edit", defaults: "Edit" })),
                        React.createElement(Button, { type: "link", danger: true, onClick: () => handleRemoveAction(index) },
                            React.createElement(Trans, { i18nKey: "remove", defaults: "Remove" }))
                    ] },
                    React.createElement("div", null,
                        React.createElement(Typography.Text, null, title),
                        action.confidentialData && (React.createElement(Typography.Text, { type: "secondary", style: { marginLeft: 8 } },
                            "[",
                            React.createElement(Trans, { i18nKey: "confidential" }),
                            "]")))));
            } }),
        list.length === 0 && (React.createElement(Alert, { message: React.createElement(Trans, { i18nKey: "scenarioMustHaveAtLeastOneAction", defaults: "A scenario must have at least one action." }), type: "warning", showIcon: true, style: { marginTop: 16 } })),
        React.createElement(Button, { type: "dashed", onClick: handleAddAction, style: { marginTop: '16px' } },
            React.createElement(PlusOutlined, null),
            " ",
            React.createElement(Trans, { i18nKey: "addAction", defaults: "Add Action" })),
        React.createElement(Modal, { title: isEditing ? t('editAction', 'Edit Action') : t('addAction', 'Add Action'), open: isModalVisible, onOk: handleOk, onCancel: handleCancel, okButtonProps: { disabled: isOkDisabled }, okText: isEditing ? t('save', 'Save') : t('add', 'Add'), destroyOnClose: true, width: 700, bodyStyle: { maxHeight: '70vh', overflowY: 'auto' } },
            React.createElement(Form, { form: form, layout: "vertical", name: "form_in_modal", initialValues: { name: modalActionData.name } },
                React.createElement(Form.Item, { name: "name", label: React.createElement(Trans, { i18nKey: "actionType", defaults: "Action Type" }), rules: [{ required: true, message: React.createElement(Trans, { i18nKey: "pleaseSelectActionType", defaults: "Please select an action type!" }) }] },
                    React.createElement(Select, { placeholder: t('selectActionPlaceholder', 'Select an action'), onChange: handleActionTypeChange, disabled: isEditing }, availableActions.map(action => (React.createElement(Select.Option, { key: action.name, value: action.name }, action.title))))),
                React.createElement(Form.Item, { name: "confidentialData", valuePropName: "checked", style: { marginBottom: 16 } },
                    React.createElement(Checkbox, null,
                        React.createElement(Trans, { i18nKey: "containsConfidentialData" }),
                        React.createElement(Typography.Text, { type: "secondary", style: { display: 'block', fontSize: '12px' } },
                            React.createElement(Trans, { i18nKey: "containsConfidentialDataTooltip" }))))),
            (selectedActionDef === null || selectedActionDef === void 0 ? void 0 : selectedActionDef.paramsSchema) && Object.keys(selectedActionDef.paramsSchema).length > 0 && (React.createElement("div", null,
                React.createElement(Typography.Title, { level: 5, style: { marginTop: 16 } },
                    React.createElement(Trans, { i18nKey: "parametersTitle", defaults: "Parameters" })),
                React.createElement(ParameterEditor, { schema: selectedActionDef.paramsSchema, formData: modalActionData.params, onChange: (params) => setModalActionData(prev => (Object.assign(Object.assign({}, prev), { params }))), availableProviders: availableProviders }))))));
};
export default Step_Actions;
